import { DESTINATION_TYPES } from '../../../containers/destination/constants/destinationTypes';
import { DestinationAPI } from '../../../edge/destinations/api/DestinationAPI';
import React from 'react';
import { RawDestination } from '../../../containers/destination/interfaces/destination';
import { GlobalSearchItem, GlobalSearchRegistryItem } from '../../interfaces/global-search';
import DestinationsAPI from '../../../containers/destination/api/DestinationsAPI';
import {
  GenericListItemContent,
  GenericListItemContentProps
} from './GenericListItemContent/GenericListItemContent';
import { GlobalSearchEntityCommonData } from './interfaces/global-search-entity';
import { isH2OEnabled } from './registryTeamSettings';

export const GLOBAL_SEARCH_DESTINATION_KEY = 'destinations';

export const GlobalSearchDestinationRegistry: GlobalSearchRegistryItem<GlobalSearchEntityCommonData> =
  {
    key: GLOBAL_SEARCH_DESTINATION_KEY,
    title: 'Destinations',
    maxItemsWhenNoFiltersApplied: 2,
    maxItemsWhenFiltersApplied: 20,
    sortOrder: 2,
    filters: [
      {
        value: GLOBAL_SEARCH_DESTINATION_KEY,
        label: 'Destinations'
      }
    ],
    viewAllRoute: '/destination',
    inMemoryFiltering: true,
    dataResolver: () => {
      const isH20Enabled = isH2OEnabled();

      const promises = [DestinationsAPI.getExistingDestinations()];

      if (isH20Enabled) {
        promises.push(DestinationAPI.getDestinations());
      }

      return Promise.all(promises).then(([{ data: standardDestinations }, edgeDestinationsRes]) => {
        const _edgeDestinations = isH20Enabled
          ? edgeDestinationsRes.data.map(GlobalSearchEnterpriseDestinationFactory)
          : [];
        const _standardDestinations = standardDestinations.map(d =>
          GlobalSearchDestinationFactory(d)
        );

        return [..._edgeDestinations, ..._standardDestinations];
      });
    },
    renderItem: item => {
      const props: GenericListItemContentProps = {
        suffixIcon: item.icon,
        suffixIconBadge: item.data.isEdge ? 'Edge' : undefined,
        primaryText: item.name,
        secondaryText: item.data.displayType,
        canUseEnterpriseLogo: !!item.data.isEdge,
        primaryLogoProps: {
          logoURL: item.data.logoUrl,
          darkModeLogoURL: item.data.darkModeLogoUrl,
          primaryColor: DESTINATION_TYPES[item.data.type]?.primaryColor,
          darkModePrimaryColor: DESTINATION_TYPES[item.data.type]?.darkModePrimaryColor
        },
        primaryLogoTooltip: item.data.displayType
      };

      return <GenericListItemContent {...props} />;
    }
  };

export function GlobalSearchDestinationFactory(
  rawDestination: RawDestination
): GlobalSearchItem<GlobalSearchEntityCommonData> {
  return {
    data: {
      type: rawDestination.dest_type,
      displayType: rawDestination.dest_type_display,
      logoUrl: rawDestination.dest_type_logo_url,
      darkModeLogoUrl:
        rawDestination.dest_type_dark_mode_logo_url ?? rawDestination.dest_type_logo_url,
      isEdge: false,
      name: rawDestination.name
    },
    key: `${rawDestination.dest_type}-standard-destination-${rawDestination.seq_id}`,
    name: rawDestination.name ?? rawDestination.dest_name,
    icon: 'destinations',
    exitURL: `/destination/d/${rawDestination.seq_id}/overview`,
    searchKeywords: [
      rawDestination.dest_type,
      rawDestination.name,
      rawDestination.dest_type_display,
      rawDestination.dest_name
    ]
  };
}

export function GlobalSearchEnterpriseDestinationFactory(
  rawDestination: RawDestination
): GlobalSearchItem<GlobalSearchEntityCommonData> {
  return {
    data: {
      type: rawDestination.destination_type.type,
      name: rawDestination.name,
      displayType: rawDestination.destination_type.display_name,
      logoUrl: rawDestination.destination_type.logo_url,
      darkModeLogoUrl: rawDestination.destination_type.dark_mode_logo_url,
      isEdge: true
    },
    key: `${rawDestination.destination_type.type}-edge-destination-${rawDestination.id}`,
    name: rawDestination.name,
    icon: 'destinations',
    exitURL: `/destination/edge/${rawDestination.id}/configuration`,
    searchKeywords: [
      rawDestination.name,
      rawDestination.destination_type.type,
      rawDestination.destination_type.display_name
    ]
  };
}
